import React, { useState, useMemo, useEffect } from 'react'
import { withRouter } from 'react-router-dom';

import AuthApi from '../api/AuthApi';
import Loader from '../components/Loader';
import useLoader from '../util/useLoader';

export const AuthContext = React.createContext();


const UserStore = ({
    KEY: 'user',
    MFA_KEY: 'mfa',
    user: null,
    mfa: null,
    init() {
        const json = localStorage.getItem(this.KEY);
        this.user = json ? JSON.parse(json) : null;

        const mfaJson = localStorage.getItem(this.MFA_KEY);
        try {
            this.mfa = mfaJson ? JSON.parse(mfaJson) : {};
        }
        catch (e) {
            this.mfa = {};
        }


        return this;
    },
    buildUserFrom(user, token, logOutUrl, actualUser){
        return {
            email: user.email,
            token,
            logOutUrl,
            user,
            actualUser,
            permissions: user.accessProfile.permissions.reduce((_, p) => {
                _[p] = 1;
                return _;
            }, {}),
            organizationalUnitRoles: user.organizationalUnitRoles,
            organizationalUnits: user.organizationalUnitRoles.reduce((_, {
                id, userId, organizationalUnit, ...roleflags
            }) => {
                if (!_.index[organizationalUnit.id]) {
                    const entry = {...organizationalUnit, roles: []};
                    _.list.push(entry);
                    _.index[organizationalUnit.id] = entry;
                }

                _.index[organizationalUnit.id].roles.push(roleflags);

                return _;
            }, {list:[], index:{}}).list,
            rolesByName: user.organizationalUnitRoles.reduce((_, our) => {
                _[our.role.name] = our;
                return _;
            }, {}),
        };
    },
    setUser(newUser) {
        this.user = newUser

        if (newUser) {
            localStorage.setItem(this.KEY, JSON.stringify(newUser));
        } else {
            localStorage.removeItem(this.KEY);
        }

        // Cancel out any mfa token
        this.deleteMfaInfo();
    },
    setMfaInfo(token, type) {
        this.mfa = { token, type, verification: true };
        localStorage.setItem(this.MFA_KEY, JSON.stringify(this.mfa));
    },
    setMfaReminder() {
        this.mfa = { ...this.mfa, reminder: true };
        localStorage.setItem(this.MFA_KEY, JSON.stringify(this.mfa));
    },
    toggleMfaVerification() {
        this.mfa.verification = !this.mfa.verification;
        localStorage.setItem(this.MFA_KEY, JSON.stringify(this.mfa));
    },
    getMfaToken() {
        return this.mfa?.token;
    },
    deleteMfaInfo() {
        this.mfa = {};
        localStorage.removeItem(this.MFA_KEY);
    },
    isAdmin: false
}).init();


function _AuthProvider(props) {
    const { children } = props;
    const [loading, errorLoading, loadFn] = useLoader(true);
    const [updateCt, setUpdateCt] = useState(0);

    const value = useMemo(() => ({
        user: UserStore.user,
        updateCt,

        async login({ email, password }) {
            // Get the token, and explicitly request a newly created challenge if none found
            const prevMfaToken = UserStore.getMfaToken();

            const response = await AuthApi.login({ email, password, resendMfaVerification: !prevMfaToken });
            const { 
                token,
                logOutUrl,
                user,
                mfaVerificationRequired,
                mfaVerificationCreated,
                mfaVerificationType 
            } = response;

            if (!mfaVerificationRequired) {
                // Build the user from returned token
                UserStore.setUser(UserStore.buildUserFrom(user, token, logOutUrl));
            }
            else if (mfaVerificationCreated) {
                // Set the token and type for the newly created verification
                UserStore.setMfaInfo(token, mfaVerificationType);
            } else {
                // Remind the user to check their email, reuse existing mfa details
                UserStore.setMfaReminder();
                UserStore.toggleMfaVerification();
            }
        
            setUpdateCt(updateCt + 1);
        },

        async verifyMfa({ otp }) {
            const mfaToken = UserStore.getMfaToken();

            const response = await AuthApi.verifyMfa({ otp, token: mfaToken });
            const { token, logOutUrl, user, } = response;
            
            UserStore.setUser(UserStore.buildUserFrom(user, token, logOutUrl));

            // Delete temporary mfa info
            UserStore.deleteMfaInfo();

            setUpdateCt(updateCt + 1);
        },

        // Do we define a route to tell the backend to CANCEL the challenge? Or not?
        cancelMfaVerification() {
            UserStore.deleteMfaInfo();
            setUpdateCt(updateCt + 1);
        },

        toggleMfaVerification() {
            UserStore.toggleMfaVerification();
            setUpdateCt(updateCt + 1);
        },

        async loginWithToken({ token }) {
            const response = await AuthApi.loginWithToken({ token });
            const { token: authToken, logOutUrl, user } = response;
            UserStore.setUser(UserStore.buildUserFrom(user, authToken, logOutUrl));
            setUpdateCt(updateCt + 1);
        },

        logout() {
            const logOutUrl = UserStore?.user?.logOutUrl;
            // Remove all dashboard filters from localStorage BEFORE redirect so it always runs
            Object.keys(localStorage).forEach((key) => {
                if (key.endsWith("-params")) {
                    localStorage.removeItem(key);
                }
            });
            UserStore.setUser();
            if (logOutUrl) {
                document.location = logOutUrl;
            } else {
                setUpdateCt(updateCt + 1);
            }
        },

        setCompletedProfile(hasCompletedProfile) {
            UserStore.user.user.hasCompletedProfile = hasCompletedProfile;
            UserStore.setUser(UserStore.user);
        },

        async impersonate({ userId }) {
            const actualUser = UserStore.user;
            const response = await AuthApi.impersonate({ userId });
            const { token, logOutUrl, user } = response;
            UserStore.setUser(UserStore.buildUserFrom(user, token, logOutUrl, actualUser));
            setUpdateCt(updateCt + 1);
        },

        async stopImpersonation() {
            const {actualUser} = UserStore.user;
            if (actualUser) {
                UserStore.setUser(actualUser);
                setUpdateCt(updateCt + 1);
            }
        },

        async verifyToken() {
            console.log("verifying user token...")
            try {
                const response = await AuthApi.verifyToken();
                if (!response.data) {
                    console.log("token is invalid or stale, user is not logged in.");
                    UserStore.setUser();
                    setUpdateCt(updateCt + 1);
                } else {
                    console.log("user is still logged in.");
                }
            } catch(e) {
                console.log("error while verifiyng token, user is not logged in.");
                UserStore.setUser();
                setUpdateCt(updateCt + 1);
            }
        },

        isAdmin: UserStore.user?.user?.role?.name === 'administrator',
        isLoggedIn: !!UserStore.user,
        // isPendingMfaVerification: !!UserStore.mfa?.required,
        mfaType: UserStore.mfa?.type,
        hasMfaReminder: !!UserStore.mfa?.reminder,
        hasMfaVerification: !!UserStore.mfa?.verification
    }), [UserStore, updateCt]);


    useEffect(() => {
        loadFn(() => value.verifyToken());
    }, []);


    return (
        <AuthContext.Provider value={value}>
            {loading ? <Loader fullscreen/> : children}
        </AuthContext.Provider>
    )
}


export function getToken() {
    return (UserStore.user || {}).token;
}

export const AuthConsumer = AuthContext.Consumer;

export const AuthProvider = withRouter(_AuthProvider);
